import { Box, rem, Stack, type StackProps, Title } from '@mantine/core';
import { Text } from '@mantine/core';

import { useMantineTheme } from '@mantine/core';
import { Banner } from '@vrplatform/components';
import type { ResponseData } from '@vrplatform/data';
import { centsToFloat, formatCurrency, type Maybe } from '@vrplatform/utils';
import type { ComponentProps } from 'react';
import { HiddenFeatureIndicator } from '../../components';

type ReservationIssue = ResponseData<'get:/reservations/{id}'>['issues'][0];

export const DrawerIssues = <
  TIssue extends {
    code: string;
    severity: ReservationIssue['severity'];
    context: object;
    message?: string;
  },
>({
  issues,
  config: ISSUES_CONFIG,
  mb,
}: {
  issues: Maybe<TIssue[]>;
  config: Record<TIssue['code'], ComponentProps<typeof IssueItem>>;
  mb?: StackProps['mb'];
}) => {
  'use memo';

  if (!issues?.length) return null;

  return (
    <Stack gap="xs" mb={mb}>
      {issues?.map((issue) => {
        const config = ISSUES_CONFIG[issue.code];
        const onlyVrpAdmins =
          issue.code === 'guestTotalsZero' ||
          issue.code === 'guestTotalsMismatch';

        if (!config || onlyVrpAdmins) {
          let text =
            config?.description ||
            ('message' in issue ? String(issue.message) : 'Unknown issue');

          const difference = (issue.context as any)?.difference;
          if (difference) {
            text += ` (${formatCurrency(centsToFloat(difference))})`;
          }
          return (
            <HiddenFeatureIndicator permission="vrp-admin" p="xs">
              <IssueItem
                key={issue.code}
                title={config?.title || issue.code}
                description={text}
              />
            </HiddenFeatureIndicator>
          );
        }

        let text = config?.description || 'Unknown issue';
        const difference = (issue.context as any)?.difference;
        if (difference) {
          text += ` (${formatCurrency(centsToFloat(difference))})`;
        }
        return (
          <IssueItem
            key={issue.code}
            title={config?.title || issue.code}
            description={text}
          />
        );
      })}
    </Stack>
  );
};

const IssueItem = ({
  description,
  title,
  type = 'warning',
}: {
  title: string;
  description: string;
  type?: ComponentProps<typeof Banner>['type'];
}) => {
  const theme = useMantineTheme();

  return (
    <Banner mounted type={type} mb={0} icon="Activity2Icon">
      <Box sx={{ flex: 1 }}>
        <Title order={5} fw={500} mb={rem(5)} c={theme.colors.neutral[7]}>
          {title}
        </Title>

        <Text size="xs" c="neutral">
          {description}
        </Text>
      </Box>
    </Banner>
  );
};
